import { useState, useEffect, useRef } from 'react'
import { AssistantView } from '../../views'
import { useGlobalState } from '../../../GlobalState'
import { useLocation } from 'react-router-dom'
import socketio from 'socket.io-client'
import { LocalStorageService } from '../../../services'
import { useTranslation } from 'react-i18next'

const AssistantAction = () => {
    // #region variables
    const urlBase = process.env.REACT_APP_CHATBOT_URL
    const localStorageService = new LocalStorageService()
    const [chatbotUrl, setChatbotUrl] = useState('')
    const { botConfig } = useGlobalState()
    const [iframeElement, setIframeElement] = useState(null)
    const [showIframe, setShowIframe] = useState(true)
    const socketRef = useRef()
    const location = useLocation()
    const conversationId = useRef()
    const { i18n } = useTranslation()
    const currentLanguage = i18n.language === 'en' ? 'english' : 'spanish'
    // #endregion variables

    // #region effects
    /**
     * @description init effect in mount component
     * @author Antonio Vazquez
     * @createdDate 01-12-25
     */
    useEffect(() => {
        window.addEventListener('beforeunload', () => handleCloseConversation())
    }, [])

    /**
     * @description effect to get iframe element
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        const iframe = document.getElementById('iframeChatbot')
        setIframeElement(iframe)
    })

    /**
     * @description effect to check #root in iframe
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        try {
        // Send message to iframe asking to validate #root
        handleCheckIframeRoot()

        // Listen for the response from the iframe
        window.addEventListener('message', (event) => {
            if (event.data.action === 'ROOT_RESULT') {
                if (!event.data.hasElements) {
                    setShowIframe(false)
                }
            }

            if (event.data && event.data.type === 'conversationId') {
                conversationId.current = event.data.id
            }
        })

        // execute verification
        setTimeout(handleCheckIframeRoot, 1000)
        } catch (e) {
            console.error(e)
        }
    }, [iframeElement, botConfig])

    /**
     * @description effect to connect socket when botConfig change
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        if (botConfig?.id) {
            if (socketRef.current) {
                socketRef.current.disconnect()
            }
            handleConnectSocketAssistant()
        }
    }, [botConfig])

    /**
     * @description effect to re-check iframe root when showIframe change
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        if (!showIframe) {
            setTimeout(() => setShowIframe(true), 0)
        } else {
            handleCheckIframeRoot()
        }
    }, [showIframe])

    /**
     * @description effect to notify socket when user close assistant view
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        return () => {
            if (location.pathname === '/assistant') {
                try {
                    if (conversationId.current) {
                        handleCloseConversation()
                    }
                } catch (e) {
                    console.error(e)
                }
            }
        }
    }, [location])

    /**
     * @description effect to change chatbot language when currentLanguage change
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    useEffect(() => {
        try {
            if (iframeElement && currentLanguage && chatbotUrl && botConfig?.id) {
                iframeElement?.contentWindow && iframeElement.contentWindow.postMessage(
                    { action: 'CHANGE_LANGUAGE', language: currentLanguage },
                    '*'
                )
            }
        } catch(e) {
            console.error(`An error occurred in language change effect: ${e}`)
        }
    }, [currentLanguage])
    // #endregion effects

    // #region methods
    /**
     * @description event to load chat
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    const handleLoadChat = () => {
        try {
            const accessToken = localStorageService.getLocalStorageItem('token', true)?.accessToken
            setChatbotUrl(`${urlBase}?botId=${botConfig?.id}${accessToken && '&clientToken=' + accessToken}&lang=${currentLanguage}`)
        } catch (e) {
            console.error(`An error occurred in handleLoadChat: ${e}`)
        }
    }

    /**
     * @description event to check iframe root content
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    const handleCheckIframeRoot = () => {
        try {
            iframeElement?.contentWindow && iframeElement.contentWindow.postMessage(
                { action: 'CHECK_ROOT' },
                '*'
            )
        } catch (e) {
            console.error(e)
        }
    }

    /**
     * @description event to connect socket for assistant
     * @author Antonio Vazquez
     * @createdDate 28-11-25
     */
    const handleConnectSocketAssistant = () => {
        try {
            // connect socket
            socketRef.current = socketio.connect(process.env.REACT_APP_SOCKET_ASSISTANT)

            socketRef.current.on('connect', () => {
                console.log('socket connected from assistant action -> ', socketRef.current.id)
            })

            // clean when dismount component
            return () => {
                if (socketRef.current) {
                    socketRef.current.disconnect()
                }
            }
        } catch (error) {
            console.error(`An error occurred in handleConnectSocketAssistant: ${error?.message}`)
        }
    }

    /**
     * @description event to close conversation
     * @author Antonio Vazquez
     * @createdDate 01-12-25
     * @return {void}
     */
    const handleCloseConversation = () => {
        try {
            if (conversationId.current) {
                const localClientId = localStorageService.getLocalStorageItem('user', true)?.userMetaData.userA3Cloud.clientId
                socketRef.current.emit('onCloseConversationAssistant', {
                    conversationId: conversationId.current,
                    clientId: localClientId
                })
            }
        } catch (e) {
            console.error(`An error occurred in handleCloseConversation: ${e}`)
        }
    }
    // #endregion methods
    return <AssistantView chatbotUrl={chatbotUrl} handleLoadChat={handleLoadChat} botConfig={botConfig} showIframe={showIframe} />
}

export { AssistantAction as default }